import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon, BellIcon, UserPlusIcon, CurrencyDollarIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { notificationAPI } from '../services/api';

const NotificationPanel = ({ onClose }) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await notificationAPI.getNotifications();
      setNotifications(response.data.notifications || []);
    } catch (e) { /* silent */ }
    finally { setLoading(false); }
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationAPI.markAllRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: 1 })));
    } catch (e) { /* silent */ }
  };

  const getIcon = (type) => {
    switch (type) {
      case 'invite': return UserPlusIcon;
      case 'bill': return CurrencyDollarIcon;
      case 'settlement': return CheckCircleIcon;
      default: return BellIcon;
    }
  };

  const getIconColor = (type) => {
    switch (type) {
      case 'invite': return 'text-primary bg-primary/10';
      case 'bill': return 'text-amber-600 bg-amber-50';
      case 'settlement': return 'text-success bg-success-bg';
      default: return 'text-slate-600 bg-slate-100';
    }
  };

  const formatTimeAgo = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000);
    if (diff < 60) return 'baru saja';
    if (diff < 3600) return `${Math.floor(diff / 60)}m lalu`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}j lalu`;
    return `${Math.floor(diff / 86400)}h lalu`;
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center"
        onClick={onClose}
      >
        <motion.div
          initial={{ y: '100%' }}
          animate={{ y: 0 }}
          exit={{ y: '100%' }}
          transition={{ type: 'spring', damping: 25, stiffness: 200 }}
          className="bg-white rounded-t-[32px] sm:rounded-3xl p-6 w-full max-w-md shadow-[0_-10px_40px_rgba(0,0,0,0.1)] max-h-[80vh] overflow-hidden flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="w-10 h-1 bg-slate-300 rounded-full mx-auto mb-6 sm:hidden"></div>

          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-slate-900">Notifikasi</h3>
            <div className="flex items-center gap-2">
              {notifications.some(n => !n.is_read) && (
                <button
                  onClick={handleMarkAllRead}
                  data-testid="mark-all-read-btn"
                  className="text-xs font-semibold text-primary hover:underline"
                >
                  Tandai semua dibaca
                </button>
              )}
              <button onClick={onClose} data-testid="close-notification-panel"
                className="w-8 h-8 rounded-full bg-surface flex items-center justify-center hover:bg-surface-hover transition-colors">
                <XMarkIcon className="w-5 h-5 text-slate-600" />
              </button>
            </div>
          </div>

          <div className="overflow-y-auto flex-1 -mx-6 px-6 space-y-3">
            {loading ? (
              <div className="text-center py-8">
                <div className="w-6 h-6 border-3 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
              </div>
            ) : notifications.length === 0 ? (
              <div className="text-center py-8">
                <BellIcon className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500 text-sm">Belum ada notifikasi</p>
              </div>
            ) : (
              notifications.map((notif) => {
                const Icon = getIcon(notif.type);
                const colorClass = getIconColor(notif.type);
                return (
                  <motion.div
                    key={notif.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`flex items-start gap-3 p-3 rounded-2xl transition-colors ${!notif.is_read ? 'bg-primary/5' : 'bg-white'}`}
                    data-testid={`notification-${notif.id}`}
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${colorClass}`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-slate-900 text-sm">{notif.title}</p>
                      <p className="text-xs text-slate-600 mt-0.5 line-clamp-2">{notif.message}</p>
                      <p className="text-[10px] text-slate-400 mt-1">{formatTimeAgo(notif.created_at)}</p>
                    </div>
                    {!notif.is_read && (
                      <div className="w-2 h-2 bg-primary rounded-full flex-shrink-0 mt-2"></div>
                    )}
                  </motion.div>
                );
              })
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default NotificationPanel;
